import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import Select from 'react-select';
import { roundDownTotal } from './Consumables/consumableHelpers';
import { ConsumableInvoiceDocument } from '../components/BillInvoicePreview';
import InvoicePreviewModal from '../components/InvoicePreviewModal';
import BillViewModal from '../components/BillViewModal';

const API_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';

// ─── Types ───────────────────────────────────────────────────────────────────

interface Patient {
  id: number;
  patient_name: string;
  age: string;
  sex: string;
  treating_hospital?: string;
  treating_doctor?: string;
  treating_details?: { doctor: string; hospital: string; department: string }[];
  case_start_date: string;
  address: string;
  patient_clients: { client_name: string; contact_number_1: string; email: string }[];
}

interface ActiveBatch {
  batch_id: number;
  consumable_id: number;
  consumable_name: string;
  category: string | null;
  priority: number | null;
  gst_percent: number | null;
  batch_no: string | null;
  hsn_sac: string | null;
  mrp: number | null;
  selling_price: number | null;
  dealer_price: number | null;
  current_quantity: number;
  expiry_date: string | null;
  vendor_name: string | null;
}

// One line the backend reported as short on stock (from the 400 detail).
interface InsufficientItem {
  batch_id: number;
  consumable_id: number;
  consumable_name: string | null;
  batch_no: string | null;
  available: number;
  requested: number;
}

interface ConsumableLineItem {
  batch_id: number | null;
  batch_display: string;
  hsn_sac: string;
  dealer_price: number | '';
  mrp: number | '';
  disc_price: number;
  discount_rate: number;
  quantity: number | '';
  gst_percent: number;
  cgst_amount: number;
  sgst_amount: number;
  subtotal: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const today = new Date().toISOString().split('T')[0];

const fmtAmt = (n: number) =>
  n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const STATUS_OPTIONS = ['Pending', 'Collected', 'Processed'];

const emptyItem = (): ConsumableLineItem => ({
  batch_id: null,
  batch_display: '',
  hsn_sac: '',
  dealer_price: '',
  mrp: '',
  disc_price: 0,
  discount_rate: 0,
  quantity: '',
  gst_percent: 0,
  cgst_amount: 0,
  sgst_amount: 0,
  subtotal: 0,
});

const computeDownstream = (item: ConsumableLineItem): ConsumableLineItem => {
  const qty = Number(item.quantity) || 0;
  const gst_percent = item.gst_percent || 0;
  const taxable_value = item.disc_price * qty;
  const cgst = taxable_value * ((gst_percent / 2) / 100);
  const sgst = taxable_value * ((gst_percent / 2) / 100);
  const subtotal = taxable_value + cgst + sgst;
  return { ...item, cgst_amount: cgst, sgst_amount: sgst, subtotal };
};

const batchDisplay = (b: ActiveBatch) =>
  `${b.consumable_name}${b.batch_no ? ` — Batch #${b.batch_no}` : ''}`;

// ─── Main Component ───────────────────────────────────────────────────────────

const CreateConsumableBill: React.FC = () => {
  const { patientId } = useParams<{ patientId: string }>();
  const navigate = useNavigate();

  const [patient, setPatient] = useState<Patient | null>(null);
  const [activeBatches, setActiveBatches] = useState<ActiveBatch[]>([]);
  // Consumables carry no shared bill number — invoiceNo is their only number.
  // Printed series (LHHS/11154/26-27). Read-only: the server assigns the real one on
  // save, so this is a preview that can go stale if someone else bills first.
  const [invoiceNo, setInvoiceNo] = useState('');
  const [billDate, setBillDate] = useState(today);
  const [status, setStatus] = useState('Pending');
  const [remarks, setRemarks] = useState('');
  const [lineItems, setLineItems] = useState<ConsumableLineItem[]>([emptyItem()]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  // Insufficient-stock modal: the short lines the last save attempt hit, plus
  // which of them the user has already restocked (visual only — the real
  // re-check happens on "Retry Save").
  const [stockIssues, setStockIssues] = useState<InsufficientItem[] | null>(null);
  const [restockedBatchIds, setRestockedBatchIds] = useState<number[]>([]);
  // Top-up modal for a single short batch (adds to that exact batch).
  const [restockTarget, setRestockTarget] = useState<InsufficientItem | null>(null);
  const [topUpQty, setTopUpQty] = useState('');
  const [toppingUp, setToppingUp] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  // Set once the bill exists on the server. Until then the preview is a draft and
  // cannot be downloaded — its invoice number is only a guess at the next one.
  const [createdBillId, setCreatedBillId] = useState<number | null>(null);

  const token = localStorage.getItem('accessToken');
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const fetchInitial = useCallback(async () => {
    if (!patientId) return;
    try {
      const [pRes, batchRes] = await Promise.all([
        axios.get(`${API_URL}/api/patients/${patientId}`, { headers }),
        axios.get(`${API_URL}/api/consumables/active-batches`, { headers }),
      ]);
      setPatient(pRes.data);
      setActiveBatches(batchRes.data);
    } catch {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => { fetchInitial(); }, [fetchInitial]);

  // The FY suffix comes off the bill date, so re-preview whenever it changes.
  useEffect(() => {
    if (!billDate) return;
    let cancelled = false;
    axios
      .get(`${API_URL}/api/billing/next-consumable-invoice-no`, { headers, params: { bill_date: billDate } })
      .then((r) => { if (!cancelled) setInvoiceNo(r.data.invoice_no); })
      .catch(() => { if (!cancelled) setInvoiceNo(''); });
    return () => { cancelled = true; };
  }, [billDate]);

  // ── line item helpers ───────────────────────────────────────────────────

  // Map an active batch onto a fresh/updated line item. Shared by the per-row
  // batch picker and the bulk "add whole category" action.
  const batchToLineItem = (batch: ActiveBatch, base: Partial<ConsumableLineItem>): ConsumableLineItem => {
    const mrp = batch.mrp ?? 0;
    // Pre-fill the discounted price with the batch's selling price (falling back to
    // MRP when no selling price is set), and back-fill the discount % to match so
    // the two stay consistent — the user can still override either.
    const discPrice = batch.selling_price ?? mrp;
    const discountRate = mrp > 0 ? ((mrp - discPrice) / mrp) * 100 : 0;
    return computeDownstream({
      ...emptyItem(),
      ...base,
      batch_id: batch.batch_id,
      batch_display: batchDisplay(batch),
      hsn_sac: batch.hsn_sac ?? '',
      dealer_price: batch.dealer_price ?? '',
      mrp,
      disc_price: discPrice,
      discount_rate: discountRate,
      gst_percent: batch.gst_percent ?? 0,
    });
  };

  const selectBatch = (idx: number, batch: ActiveBatch) => {
    setLineItems((prev) => prev.map((item, i) =>
      i === idx ? batchToLineItem(batch, { quantity: item.quantity }) : item
    ));
  };

  // Bulk-add every in-stock batch of a category as line items (input only —
  // category is never shown on the client-facing invoice). Skips batches that
  // are already on the bill and drops the leading blank placeholder row.
  const addCategoryToBill = (category: string) => {
    const matching = activeBatches
      .filter((b) => (b.category ?? '') === category)
      // Insert in the category's configured order: priority (asc, blank last) → name → batch.
      .sort((a, b) => {
        const pa = a.priority ?? Infinity, pb = b.priority ?? Infinity;
        if (pa !== pb) return pa - pb;
        const byName = a.consumable_name.localeCompare(b.consumable_name);
        if (byName !== 0) return byName;
        return (a.batch_no ?? '').localeCompare(b.batch_no ?? '');
      });
    if (matching.length === 0) { toast.error('No in-stock items in this category'); return; }
    setLineItems((prev) => {
      const kept = prev.filter((i) => i.batch_id !== null);
      const existingIds = new Set(kept.map((i) => i.batch_id));
      const toAdd = matching
        .filter((b) => !existingIds.has(b.batch_id))
        .map((b) => batchToLineItem(b, { quantity: 1 }));
      if (toAdd.length === 0) { toast('All items in this category are already added'); return prev; }
      toast.success(`Added ${toAdd.length} item(s) from "${category}"`);
      const next = [...kept, ...toAdd];
      return next.length > 0 ? next : [emptyItem()];
    });
    setSelectedCategories((prev) => (prev.includes(category) ? prev : [...prev, category]));
  };

  // Remove a category tag and pull its rows back out of the bill.
  const removeCategoryFromBill = (category: string) => {
    const batchIds = new Set(
      activeBatches.filter((b) => (b.category ?? '') === category).map((b) => b.batch_id)
    );
    setLineItems((prev) => {
      const next = prev.filter((i) => i.batch_id === null || !batchIds.has(i.batch_id));
      return next.length > 0 ? next : [emptyItem()];
    });
    setSelectedCategories((prev) => prev.filter((c) => c !== category));
  };

  const updateItem = (idx: number, field: keyof ConsumableLineItem, value: number | string) => {
    setLineItems((prev) => prev.map((item, i) => {
      if (i !== idx) return item;
      const next = { ...item, [field]: value };
      const mrp = Number(next.mrp) || 0;

      if (field === 'discount_rate') {
        next.disc_price = mrp - (mrp * ((next.discount_rate || 0) / 100));
      } else if (field === 'disc_price') {
        next.discount_rate = mrp > 0 ? ((mrp - next.disc_price) / mrp) * 100 : 0;
      } else if (field === 'mrp') {
        next.disc_price = mrp - (mrp * ((next.discount_rate || 0) / 100));
      } else if (field === 'cgst_amount' || field === 'sgst_amount') {
        const taxable = next.disc_price * (Number(next.quantity) || 0);
        next.subtotal = taxable + next.cgst_amount + next.sgst_amount;
        return next;
      }

      return computeDownstream(next);
    }));
  };

  const removeItem = (idx: number) =>
    setLineItems((prev) => prev.filter((_, i) => i !== idx));

  // ── save ────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!billDate) { toast.error('Bill date is required'); return; }
    if (lineItems.length === 0) { toast.error('Add at least one item'); return; }
    if (lineItems.some((i) => !i.batch_id)) { toast.error('Select a batch for each row'); return; }
    if (lineItems.some((i) => !i.quantity || Number(i.quantity) <= 0)) {
      toast.error('Enter quantity for each row');
      return;
    }

    setSaving(true);
    try {
      await axios.post(
        `${API_URL}/api/consumables/invoice`,
        {
          items: lineItems.map((i) => ({ batch_id: i.batch_id, quantity: Number(i.quantity) })),
          reason: `Consumable Invoice ${invoiceNo}`,
        },
        { headers },
      );

      const billingPayload = {
        patient_id: patient?.id,
        bill_date: billDate,
        // Consumables have no billing period or expected-payment date — the columns
        // are still NOT NULL in the DB, so mirror the bill date into them.
        expected_payment_date: null,
        invoice_period_start: billDate,
        invoice_period_end: billDate,
        status,
        bill_type: 'consumables',
        remarks: remarks || null,
        line_items: lineItems.map((item, idx) => ({
          s_no: idx + 1,
          particulars: item.batch_display || 'Consumable',
          rate: item.disc_price,
          details: item.hsn_sac ? `HSN/SAC: ${item.hsn_sac}` : '',
          shift_type: null,
          shifts_count: Number(item.quantity),
          total_amount: item.subtotal,
          is_miscellaneous: false,
          item_start_date: null,
          item_end_date: null,
          has_gst: item.gst_percent > 0,
          gst_percent: item.gst_percent > 0 ? item.gst_percent : null,
          section_type: 'consumables',
        })),
        consumable_items: lineItems.map((item) => ({
          batch_id: item.batch_id!,
          quantity: Number(item.quantity),
        })),
      };

      const res = await axios.post(`${API_URL}/api/billing/bills`, billingPayload, { headers });
      toast.success('Invoice saved successfully — stock deducted');
      // Hand straight over to the saved bill so it can be downloaded with the real
      // server-assigned invoice number (closing that modal leaves the screen).
      setShowPreview(false);
      setCreatedBillId(res.data.id);
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      // Structured insufficient-stock error → open the full restock modal
      // instead of a one-line toast (bills can have dozens of short items).
      if (detail && typeof detail === 'object' && detail.code === 'insufficient_stock' && Array.isArray(detail.items)) {
        setRestockedBatchIds([]);
        // The stock modal sits below the preview overlay, so drop the preview when
        // the save was kicked off from inside it.
        setShowPreview(false);
        setStockIssues(detail.items as InsufficientItem[]);
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Failed to create bill');
      }
    } finally {
      setSaving(false);
    }
  };

  // Open the top-up modal for a short line, defaulting the quantity to exactly
  // the shortfall so one click typically fixes it.
  const openRestock = (item: InsufficientItem) => {
    setRestockTarget(item);
    setTopUpQty(String(Math.max(1, item.requested - item.available)));
  };

  // Add stock to the exact batch on the bill line via the adjustment ledger
  // (positive quantity_changed), so the line can be billed on Retry Save.
  const submitTopUp = async () => {
    if (!restockTarget) return;
    const qty = parseInt(topUpQty, 10);
    if (isNaN(qty) || qty < 1) { toast.error('Enter a quantity of at least 1'); return; }
    setToppingUp(true);
    try {
      await axios.post(
        `${API_URL}/api/consumables/batches/${restockTarget.batch_id}/adjust`,
        { quantity_changed: qty, reason: `Restock during billing ${invoiceNo}` },
        { headers },
      );
      toast.success(`Added ${qty} to ${restockTarget.consumable_name || 'batch'}`);
      setRestockedBatchIds(prev => Array.from(new Set([...prev, restockTarget.batch_id])));
      setRestockTarget(null);
      setTopUpQty('');
      fetchInitial();  // refresh available stock for the eventual Retry Save
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Failed to add stock');
    } finally {
      setToppingUp(false);
    }
  };

  // Re-run the whole save; the backend re-validates every line authoritatively.
  const retrySave = () => {
    setStockIssues(null);
    setRestockedBatchIds([]);
    handleSave();
  };

  // The unsaved invoice is never downloadable — the PDF is taken from the saved
  // bill (BillViewModal) once the server has assigned the real invoice number.

  // ── computed ─────────────────────────────────────────────────────────────

  const totalExGst = lineItems.reduce((sum, i) => {
    return sum + i.disc_price * (Number(i.quantity) || 0);
  }, 0);
  const totalCgst = lineItems.reduce((s, i) => s + i.cgst_amount, 0);
  const totalSgst = lineItems.reduce((s, i) => s + i.sgst_amount, 0);
  // Payable is floored to whole rupees — see roundDownTotal(). This is the
  // figure shown here, printed on the invoice and stored on the bill.
  const itemsTotal = lineItems.reduce((s, i) => s + i.subtotal, 0);
  const grandTotal = roundDownTotal(itemsTotal);
  // The paise dropped by the floor, shown to the biller so the summary
  // visibly adds up instead of the total silently disagreeing with the parts.
  const roundOff = Math.round((itemsTotal - grandTotal) * 100) / 100;

  // ── styles ────────────────────────────────────────────────────────────────

  const s: Record<string, React.CSSProperties> = {
    page: { padding: '2rem', backgroundColor: '#f8fafc', minHeight: '100vh' },
    topBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' },
    backBtn: { background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', fontSize: '0.9rem' },
    title: { margin: 0, fontSize: '1.5rem', fontWeight: 700, color: '#0f172a' },
    subtitle: { margin: '3px 0 0', fontSize: '0.85rem', color: '#64748b' },
    actions: { display: 'flex', gap: '10px' },
    card: { backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '1.5rem', marginBottom: '1.5rem' },
    sectionTitle: { margin: '0 0 1rem', fontSize: '0.95rem', fontWeight: 700, color: '#0f172a', borderBottom: '1px solid #f1f5f9', paddingBottom: '8px' },
    patientInfo: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.75rem' },
    infoLabel: { fontSize: '0.72rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase' as const, letterSpacing: '0.04em' },
    infoValue: { fontSize: '0.88rem', color: '#334155', marginTop: '2px' },
    fieldLabel: { fontSize: '0.75rem', fontWeight: 600, color: '#64748b', marginBottom: '4px', display: 'block', textTransform: 'uppercase' as const, letterSpacing: '0.04em' },
    input: { width: '100%', padding: '0.55rem 0.75rem', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.88rem', boxSizing: 'border-box' as const },
    select: { width: '100%', padding: '0.55rem 0.75rem', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.88rem', boxSizing: 'border-box' as const, backgroundColor: 'white' },
    table: { width: '100%', borderCollapse: 'collapse' as const, tableLayout: 'fixed' as const, marginBottom: '0.5rem' },
    th: { padding: '0.55rem 0.5rem', fontSize: '0.7rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' as const, letterSpacing: '0.04em', backgroundColor: '#f8fafc', borderBottom: '2px solid #e2e8f0', textAlign: 'left' as const },
    td: { padding: '0.4rem 0.4rem', borderBottom: '1px solid #f1f5f9', verticalAlign: 'middle' as const },
    cellInput: { width: '100%', padding: '0.38rem 0.45rem', borderRadius: '5px', border: '1px solid #e2e8f0', fontSize: '0.8rem', boxSizing: 'border-box' as const },
    readonlyCell: { fontSize: '0.82rem', color: '#334155', textAlign: 'right' as const },
    summaryBox: { backgroundColor: '#f8fafc', borderRadius: '8px', padding: '1rem', border: '1px solid #e2e8f0', minWidth: '300px' },
    summaryLine: { display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: '0.88rem', color: '#64748b' },
    grandTotalLine: { display: 'flex', justifyContent: 'space-between', padding: '8px 0', fontSize: '1.05rem', fontWeight: 700, color: '#0f172a', borderTop: '2px solid #e2e8f0', marginTop: '4px' },
    deleteBtn: { padding: '0.28rem 0.45rem', border: 'none', borderRadius: '4px', cursor: 'pointer', backgroundColor: '#fee2e2', color: '#991b1b', fontSize: '0.78rem' },
    textarea: { width: '100%', padding: '0.55rem 0.75rem', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.88rem', resize: 'vertical' as const, minHeight: '80px', boxSizing: 'border-box' as const },
  };

  const btn = (variant: string): React.CSSProperties => ({
    padding: '0.55rem 1.2rem', borderRadius: '8px', cursor: 'pointer',
    fontSize: '0.88rem', fontWeight: 600,
    backgroundColor: variant === 'primary' ? '#2563eb' : 'white',
    color: variant === 'primary' ? 'white' : '#334155',
    border: variant === 'primary' ? 'none' : '1px solid #cbd5e1',
    opacity: saving ? 0.7 : 1,
  });

  if (loading) return <div style={s.page}><div style={{ textAlign: 'center', color: '#94a3b8', paddingTop: '4rem' }}>Loading…</div></div>;

  const client = patient?.patient_clients?.[0];

  const batchOptions = activeBatches.map((b) => ({
    value: b.batch_id,
    label: batchDisplay(b),
    batch: b,
  }));

  const categoryOptions = Array.from(
    new Set(activeBatches.map((b) => b.category).filter(Boolean) as string[])
  )
    .filter((c) => !selectedCategories.includes(c))
    .sort((a, b) => a.localeCompare(b))
    .map((c) => ({ value: c, label: c }));

  return (
    <div style={s.page}>
      {/* Top bar */}
      <div style={s.topBar}>
        <div>
          <button style={s.backBtn} onClick={() => navigate(patient ? `/billing/patient/${patient.id}` : '/billing')}>
            ← Back
          </button>
          <h2 style={s.title}>New Consumable Invoice</h2>
          {patient && <p style={s.subtitle}>{patient.patient_name} — CID: LHHS{patient.id}</p>}
        </div>
        <div style={s.actions}>
          <button style={btn('outline')} onClick={() => setShowPreview(true)}>Preview Invoice</button>
          <button style={btn('primary')} onClick={handleSave} disabled={saving || createdBillId !== null}>
            {saving ? 'Creating…' : 'Create Bill'}
          </button>
        </div>
      </div>

      {/* Client details */}
      {patient && (
        <div style={s.card}>
          <p style={s.sectionTitle}>Client Details</p>
          <div style={s.patientInfo}>
            {[
              { label: 'Client', value: client?.client_name || '—' },
              { label: 'Contact', value: client?.contact_number_1 || '—' },
              { label: 'CID', value: `LHHS${patient.id}` },
              { label: 'Sex', value: patient.sex || '—' },
              { label: 'Patient', value: patient.patient_name },
              { label: 'Age', value: patient.age || '—' },
              { label: 'Hospital', value: patient.treating_details?.[0]?.hospital || patient.treating_hospital || '—' },
              { label: 'Doctor', value: patient.treating_details?.[0]?.doctor || patient.treating_doctor || '—' },
            ].map(({ label, value }) => (
              <div key={label}>
                <div style={s.infoLabel}>{label}</div>
                <div style={{ ...s.infoValue, fontWeight: label === 'Client' ? 700 : undefined }}>{value}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Bill details */}
      <div style={s.card}>
        <p style={s.sectionTitle}>Bill Details</p>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <label style={s.fieldLabel}>Invoice Number</label>
            <input type="text" style={{ ...s.input, background: '#f3f4f6', cursor: 'not-allowed' }}
              value={invoiceNo || '—'} readOnly
              title="Assigned automatically when the invoice is saved" />
            <div style={{ fontSize: '0.72rem', color: '#92400e', marginTop: '0.25rem' }}>
              Provisional — the final number is assigned when you create the bill.
            </div>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <label style={s.fieldLabel}>Bill Date</label>
            <input type="date" style={s.input} value={billDate} onChange={(e) => setBillDate(e.target.value)} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <label style={s.fieldLabel}>Status</label>
            <select style={s.select} value={status} onChange={(e) => setStatus(e.target.value)}>
              {STATUS_OPTIONS.map((o) => <option key={o}>{o}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Line items */}
      <div style={s.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '0.5rem' }}>
          <p style={{ ...s.sectionTitle, marginBottom: 0 }}>Consumable Items</p>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {selectedCategories.map((cat) => (
              <span
                key={cat}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: '999px', padding: '0.2rem 0.35rem 0.2rem 0.6rem', fontSize: '0.75rem', fontWeight: 600 }}
              >
                {cat}
                <button
                  type="button"
                  onClick={() => removeCategoryFromBill(cat)}
                  title={`Remove "${cat}" items`}
                  style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 16, height: 16, borderRadius: '999px', border: 'none', background: '#dbeafe', color: '#1d4ed8', cursor: 'pointer', fontSize: '0.8rem', lineHeight: 1, padding: 0 }}
                >
                  ×
                </button>
              </span>
            ))}
            <span style={{ fontSize: '0.78rem', color: '#64748b', fontWeight: 600 }}>Add whole category:</span>
            <div style={{ minWidth: 220 }}>
              <Select
                options={categoryOptions}
                value={null}
                placeholder="Select category…"
                isDisabled={categoryOptions.length === 0}
                menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                onChange={(opt) => { if (opt) addCategoryToBill(opt.value); }}
              />
            </div>
          </div>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={{ ...s.th, width: '3%',  textAlign: 'center' }}>#</th>
                <th style={{ ...s.th, width: '19%' }}>Item / Batch</th>
                <th style={{ ...s.th, width: '7%'  }}>HSN/SAC</th>
                <th style={{ ...s.th, width: '7%',  textAlign: 'right' }}>Dealer Price ₹</th>
                <th style={{ ...s.th, width: '7%',  textAlign: 'right' }}>MRP ₹</th>
                <th style={{ ...s.th, width: '6%',  textAlign: 'right' }}>Disc. %</th>
                <th style={{ ...s.th, width: '7%',  textAlign: 'right' }}>Disc. Price ₹</th>
                <th style={{ ...s.th, width: '5%',  textAlign: 'center' }}>Qty</th>
                <th style={{ ...s.th, width: '5%',  textAlign: 'center' }}>GST %</th>
                <th style={{ ...s.th, width: '7%',  textAlign: 'right' }}>CGST ₹</th>
                <th style={{ ...s.th, width: '7%',  textAlign: 'right' }}>SGST ₹</th>
                <th style={{ ...s.th, width: '8%',  textAlign: 'right' }}>Subtotal ₹</th>
                <th style={{ ...s.th, width: '3%'  }}></th>
              </tr>
            </thead>
            <tbody>
              {lineItems.map((item, idx) => (
                <tr key={idx}>
                  <td style={{ ...s.td, textAlign: 'center', color: '#94a3b8', fontSize: '0.8rem' }}>{idx + 1}</td>

                  {/* Item / Batch */}
                  <td style={s.td}>
                    <Select
                      options={batchOptions}
                      value={item.batch_id ? batchOptions.find((o) => o.value === item.batch_id) ?? null : null}
                      onChange={(opt) => { if (opt) selectBatch(idx, opt.batch); }}
                      placeholder="Search item or batch…"
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                    />
                  </td>

                  {/* HSN/SAC */}
                  <td style={s.td}>
                    <input
                      type="text"
                      style={s.cellInput}
                      value={item.hsn_sac}
                      onChange={(e) => updateItem(idx, 'hsn_sac', e.target.value)}
                      placeholder="HSN"
                    />
                  </td>

                  {/* Dealer Price — read-only internal reference */}
                  <td style={s.td}>
                    <input
                      type="number"
                      style={{ ...s.cellInput, textAlign: 'right', backgroundColor: '#f8fafc', color: '#64748b' }}
                      value={item.dealer_price}
                      readOnly
                      disabled
                      placeholder="—"
                    />
                  </td>

                  {/* MRP */}
                  <td style={s.td}>
                    <input
                      type="number"
                      style={{ ...s.cellInput, textAlign: 'right' }}
                      value={item.mrp}
                      onChange={(e) => updateItem(idx, 'mrp', e.target.value === '' ? '' : Number(e.target.value))}
                      placeholder="0.00"
                      min={0}
                    />
                  </td>

                  {/* Disc. % */}
                  <td style={s.td}>
                    <input
                      type="number"
                      style={{ ...s.cellInput, textAlign: 'right' }}
                      value={item.discount_rate ? Math.trunc(item.discount_rate) : ''}
                      onChange={(e) => updateItem(idx, 'discount_rate', Number(e.target.value) || 0)}
                      placeholder="0"
                      min={0}
                      max={100}
                    />
                  </td>

                  {/* Disc. Price ₹ */}
                  <td style={s.td}>
                    <input
                      type="number"
                      style={{ ...s.cellInput, textAlign: 'right' }}
                      value={item.disc_price || ''}
                      onChange={(e) => updateItem(idx, 'disc_price', Number(e.target.value) || 0)}
                      placeholder="0.00"
                      min={0}
                    />
                  </td>

                  {/* Qty */}
                  <td style={s.td}>
                    <input
                      type="number"
                      style={{ ...s.cellInput, textAlign: 'center' }}
                      value={item.quantity}
                      onChange={(e) => updateItem(idx, 'quantity', e.target.value)}
                      placeholder="0"
                      min={1}
                    />
                  </td>

                  {/* GST % */}
                  <td style={s.td}>
                    <input
                      type="number"
                      style={{ ...s.cellInput, textAlign: 'center' }}
                      value={item.gst_percent || ''}
                      onChange={(e) => updateItem(idx, 'gst_percent', Number(e.target.value) || 0)}
                      placeholder="0"
                      min={0}
                    />
                  </td>

                  <td style={s.td}>
                    <input
                      type="number"
                      style={{ ...s.cellInput, textAlign: 'right' }}
                      value={item.cgst_amount || ''}
                      onChange={(e) => updateItem(idx, 'cgst_amount', Number(e.target.value) || 0)}
                      placeholder="0.00"
                      min={0}
                    />
                  </td>
                  <td style={s.td}>
                    <input
                      type="number"
                      style={{ ...s.cellInput, textAlign: 'right' }}
                      value={item.sgst_amount || ''}
                      onChange={(e) => updateItem(idx, 'sgst_amount', Number(e.target.value) || 0)}
                      placeholder="0.00"
                      min={0}
                    />
                  </td>
                  <td style={{ ...s.td, textAlign: 'right', fontWeight: 700, fontSize: '0.85rem', color: '#0f172a' }}>
                    {item.subtotal > 0 ? `₹${fmtAmt(item.subtotal)}` : '—'}
                  </td>
                  <td style={{ ...s.td, textAlign: 'center' }}>
                    <button style={s.deleteBtn} onClick={() => removeItem(idx)}>✕</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <button
          onClick={() => setLineItems((p) => [...p, emptyItem()])}
          style={{
            padding: '0.3rem 0.8rem', borderRadius: '5px', cursor: 'pointer',
            fontSize: '0.78rem', fontWeight: 500, border: '1px dashed #7dd3fc',
            backgroundColor: '#f0f9ff', color: '#0369a1', marginBottom: '1rem',
          }}
        >
          + Add Row
        </button>

        {grandTotal > 0 && (
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <div style={s.summaryBox}>
              <div style={s.summaryLine}><span>Subtotal (ex-GST)</span><span>₹{fmtAmt(totalExGst)}</span></div>
              <div style={s.summaryLine}><span>Total CGST</span><span>₹{fmtAmt(totalCgst)}</span></div>
              <div style={s.summaryLine}><span>Total SGST</span><span>₹{fmtAmt(totalSgst)}</span></div>
              {roundOff > 0 && (
                <div style={s.summaryLine}><span>Rounded Off</span><span>− ₹{fmtAmt(roundOff)}</span></div>
              )}
              <div style={s.grandTotalLine}><span>Net Total Payable</span><span>₹{fmtAmt(grandTotal)}</span></div>
            </div>
          </div>
        )}
      </div>

      {/* Remarks */}
      <div style={s.card}>
        <p style={s.sectionTitle}>Remarks</p>
        <textarea
          style={s.textarea}
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          placeholder="Any additional notes for this consumable invoice…"
        />
      </div>

      {/* Bottom action row */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '0.5rem' }}>
        <button style={btn('outline')} onClick={() => setShowPreview(true)}>Preview Invoice</button>
        <button style={btn('primary')} onClick={handleSave} disabled={saving || createdBillId !== null}>
          {saving ? 'Creating…' : 'Create Bill'}
        </button>
      </div>

      {/* Draft preview — no download until the bill is actually created */}
      {showPreview && patient && (
        <InvoicePreviewModal
          draft
          createLabel="Create Bill & Download"
          onCreate={handleSave}
          creating={saving}
          onClose={() => setShowPreview(false)}
        >
          <ConsumableInvoiceDocument
            patient={patient}
            billNo={0}
            invoiceNo={invoiceNo}
            billDate={billDate}
            periodStart={billDate}
            periodEnd={billDate}
            lineItems={[]}
            consumableItems={lineItems.map((item) => ({
              particulars: item.batch_display || 'Consumable',
              hsn_sac: item.hsn_sac,
              mrp: item.mrp !== '' ? item.mrp : null,
              discount_rate: item.discount_rate,
              disc_price: item.disc_price,
              quantity: Number(item.quantity) || 0,
              gst_percent: item.gst_percent,
              cgst_amount: item.cgst_amount,
              sgst_amount: item.sgst_amount,
              subtotal: item.subtotal,
            }))}
            remarks={remarks}
            billType="consumables"
            createdAt={new Date().toISOString()}
          />
        </InvoicePreviewModal>
      )}

      {/* Saved bill — the only place a consumable invoice PDF can be taken from */}
      {createdBillId !== null && patient && (
        <BillViewModal
          billId={createdBillId}
          patient={patient as any}
          onClose={() => navigate(`/billing/patient/${patient.id}`)}
        />
      )}

      {/* ── Insufficient-stock modal (lists every short line + restock) ── */}
      {stockIssues && (
        <div
          onClick={() => setStockIssues(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: 'white', borderRadius: '1rem', boxShadow: '0 25px 50px rgba(0,0,0,0.2)', width: '100%', maxWidth: 720, maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
          >
            <div style={{ padding: '1.1rem 1.4rem', borderBottom: '1px solid #e2e8f0' }}>
              <h2 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 800, color: '#b91c1c' }}>Not enough stock</h2>
              <p style={{ margin: '0.2rem 0 0', fontSize: '0.82rem', color: '#64748b' }}>
                {stockIssues.length} item{stockIssues.length === 1 ? '' : 's'} on this bill exceed available stock. Restock them, then retry — nothing has been saved or deducted yet.
              </p>
            </div>

            <div style={{ overflowY: 'auto', padding: '0.5rem 1.4rem 1rem' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.84rem' }}>
                <thead>
                  <tr style={{ textAlign: 'left', color: '#64748b', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    <th style={{ padding: '0.5rem 0.4rem' }}>Item / Batch</th>
                    <th style={{ padding: '0.5rem 0.4rem', textAlign: 'right' }}>Available</th>
                    <th style={{ padding: '0.5rem 0.4rem', textAlign: 'right' }}>Requested</th>
                    <th style={{ padding: '0.5rem 0.4rem', textAlign: 'right' }}>Short by</th>
                    <th style={{ padding: '0.5rem 0.4rem' }} />
                  </tr>
                </thead>
                <tbody>
                  {stockIssues.map((it) => {
                    const done = restockedBatchIds.includes(it.batch_id);
                    return (
                      <tr key={it.batch_id} style={{ borderTop: '1px solid #eef2f7' }}>
                        <td style={{ padding: '0.55rem 0.4rem' }}>
                          <div style={{ fontWeight: 600, color: '#1e293b' }}>{it.consumable_name || 'Consumable'}</div>
                          <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>Batch #{it.batch_no || it.batch_id}</div>
                        </td>
                        <td style={{ padding: '0.55rem 0.4rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{it.available}</td>
                        <td style={{ padding: '0.55rem 0.4rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{it.requested}</td>
                        <td style={{ padding: '0.55rem 0.4rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: '#b91c1c', fontWeight: 700 }}>{it.requested - it.available}</td>
                        <td style={{ padding: '0.55rem 0.4rem', textAlign: 'right' }}>
                          {done ? (
                            <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#059669' }}>✓ Restocked</span>
                          ) : (
                            <button
                              onClick={() => openRestock(it)}
                              style={{ padding: '0.35rem 0.8rem', background: 'linear-gradient(135deg,#10b981,#059669)', color: 'white', border: 'none', borderRadius: '0.4rem', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 700 }}
                            >
                              + Restock
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.6rem', padding: '1rem 1.4rem', borderTop: '1px solid #e2e8f0', background: '#f8fafc' }}>
              <button
                onClick={() => window.open('/administration/consumables', '_blank')}
                title="Open the full Consumables inventory in a new tab to add a new batch or restock"
                style={{ padding: '0.5rem 1.1rem', background: 'white', border: '1px solid #cbd5e1', borderRadius: '0.4rem', cursor: 'pointer', fontSize: '0.82rem', color: '#334155', fontWeight: 600 }}
              >
                Open Consumables ↗
              </button>
              <div style={{ display: 'flex', gap: '0.6rem' }}>
              <button onClick={() => setStockIssues(null)} style={{ padding: '0.5rem 1.1rem', background: 'none', border: '1px solid #e2e8f0', borderRadius: '0.4rem', cursor: 'pointer', fontSize: '0.85rem', color: '#64748b', fontWeight: 600 }}>
                Close
              </button>
              <button
                onClick={retrySave}
                disabled={saving}
                style={{ padding: '0.5rem 1.4rem', background: saving ? '#94a3b8' : 'linear-gradient(135deg,#2563eb,#1d4ed8)', color: 'white', border: 'none', borderRadius: '0.4rem', cursor: saving ? 'not-allowed' : 'pointer', fontSize: '0.85rem', fontWeight: 700 }}
              >
                {saving ? 'Saving…' : 'Retry Save'}
              </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Top-up modal: add stock to the exact short batch ── */}
      {restockTarget && (
        <div
          onClick={() => !toppingUp && setRestockTarget(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: '1rem' }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: 'white', borderRadius: '1rem', boxShadow: '0 25px 50px rgba(0,0,0,0.2)', width: '100%', maxWidth: 420, overflow: 'hidden' }}
          >
            <div style={{ padding: '1.1rem 1.4rem', borderBottom: '1px solid #e2e8f0' }}>
              <h2 style={{ margin: 0, fontSize: '1.02rem', fontWeight: 800, color: '#1e293b' }}>Add Stock</h2>
              <p style={{ margin: '0.2rem 0 0', fontSize: '0.8rem', color: '#64748b' }}>
                {restockTarget.consumable_name || 'Consumable'} — Batch #{restockTarget.batch_no || restockTarget.batch_id}
              </p>
            </div>
            <div style={{ padding: '1.3rem 1.4rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', color: '#64748b', marginBottom: '0.8rem' }}>
                <span>Available now: <strong style={{ color: '#1e293b' }}>{restockTarget.available}</strong></span>
                <span>Bill needs: <strong style={{ color: '#1e293b' }}>{restockTarget.requested}</strong></span>
              </div>
              <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#475569', marginBottom: '0.3rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Quantity to add</label>
              <input
                autoFocus
                type="number"
                min="1"
                value={topUpQty}
                onChange={e => setTopUpQty(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') submitTopUp(); }}
                style={{ width: '100%', padding: '0.55rem 0.75rem', border: '1px solid #e2e8f0', borderRadius: '0.4rem', fontSize: '1rem', fontWeight: 700, outline: 'none', boxSizing: 'border-box', color: '#1e293b' }}
              />
              <p style={{ margin: '0.5rem 0 0', fontSize: '0.75rem', color: '#94a3b8' }}>
                Adds to this exact batch and is recorded in the stock ledger.
              </p>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.6rem', padding: '1rem 1.4rem', borderTop: '1px solid #e2e8f0', background: '#f8fafc' }}>
              <button onClick={() => setRestockTarget(null)} disabled={toppingUp} style={{ padding: '0.5rem 1.1rem', background: 'none', border: '1px solid #e2e8f0', borderRadius: '0.4rem', cursor: 'pointer', fontSize: '0.85rem', color: '#64748b', fontWeight: 600 }}>
                Cancel
              </button>
              <button onClick={submitTopUp} disabled={toppingUp} style={{ padding: '0.5rem 1.4rem', background: toppingUp ? '#94a3b8' : 'linear-gradient(135deg,#10b981,#059669)', color: 'white', border: 'none', borderRadius: '0.4rem', cursor: toppingUp ? 'not-allowed' : 'pointer', fontSize: '0.85rem', fontWeight: 700 }}>
                {toppingUp ? 'Adding…' : '+ Add Stock'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateConsumableBill;
